import React, { useState, useEffect } from 'react';
import { getFinanceiroMes, getFinanceiroEvolucao } from '../api';
import ContasFixasPage from './ContasFixasPage';
import GastosPage from './GastosPage';
import PrecificacaoForm from './PrecificacaoForm';
import RelatorioFinanceiro from './RelatorioFinanceiro';

const fmt = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const STATUS_COLOR = { pago: 'var(--g)', pendente: 'var(--c4)', atrasado: 'var(--r)', vencendo: 'var(--y)' };
const STATUS_LABEL = { pago: '✓', pendente: '○', atrasado: '⚠', vencendo: '⏰' };

function MesPage({ onToast }) {
  const hoje = new Date();
  const [mesAno, setMesAno] = useState(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getFinanceiroMes(mesAno + '-01');
      setDados(d);
    } catch (err) { onToast(err.message, 'error'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [mesAno]);

  const navMes = delta => {
    const [y, m] = mesAno.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMesAno(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const [mesLabel] = mesAno.split('-').map(Number);
  const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const [ano, mes] = mesAno.split('-').map(Number);
  const label = `${MESES[mes - 1]} ${ano}`;

  const GRUPOS = {
    insumo: { label: '🥛 Insumos', color: 'var(--b)' },
    mao_obra: { label: '👩‍🍳 Mão de obra', color: 'var(--g)' },
    fixa: { label: '🏠 Fixas', color: 'var(--y)' },
    variavel_custo: { label: '🔥 Variáveis', color: 'var(--c4)' },
    variavel_venda: { label: '💳 Taxas', color: 'var(--r)' },
    investimento: { label: '🔧 Investimentos', color: 'var(--c3)' },
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--c4)' }}>Carregando...</div>;

  const gp = dados?.gastosPorGrupo || {};
  const contas = dados?.contas || [];
  const contasAlert = contas.filter(c => ['atrasado', 'vencendo'].includes(c.status));

  return (
    <div>
      {/* Seletor de mês */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button className="btn btn3 btn-sm" onClick={() => navMes(-1)}>‹</button>
        <strong style={{ fontSize: 16, minWidth: 100, textAlign: 'center' }}>{label}</strong>
        <button className="btn btn3 btn-sm" onClick={() => navMes(1)}>›</button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
        <div className="c" style={{ padding: '12px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'var(--c4)', marginBottom: 2 }}>Faturamento</div>
          <strong style={{ fontSize: 16, color: 'var(--g)' }}>{fmt(dados?.faturamento)}</strong>
        </div>
        <div className="c" style={{ padding: '12px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'var(--c4)', marginBottom: 2 }}>Gastos</div>
          <strong style={{ fontSize: 16, color: 'var(--r)' }}>{fmt(dados?.totalGastos)}</strong>
        </div>
        <div className="c" style={{ padding: '12px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'var(--c4)', marginBottom: 2 }}>Lucro</div>
          <strong style={{ fontSize: 16, color: (dados?.lucro || 0) >= 0 ? 'var(--g)' : 'var(--r)' }}>
            {fmt(dados?.lucro)}
          </strong>
          {dados?.margemLucro != null && (
            <div style={{ fontSize: 10, color: 'var(--c4)' }}>{(dados.margemLucro * 100).toFixed(0)}%</div>
          )}
        </div>
      </div>

      {/* Alertas de contas */}
      {contasAlert.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {contasAlert.map(c => (
            <div key={c.id} style={{ padding: '8px 12px', background: c.status === 'atrasado' ? 'var(--r3)' : 'var(--y3,#fefce8)', borderRadius: 10, marginBottom: 6, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: STATUS_COLOR[c.status] }}>{STATUS_LABEL[c.status]} {c.nome}</span>
              <strong>{fmt(c.valor_efetivo)}</strong>
            </div>
          ))}
        </div>
      )}

      {/* Gastos por grupo */}
      <div className="c" style={{ padding: '12px 14px', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Gastos por tipo</div>
        {Object.entries(GRUPOS).map(([k, { label, color }]) => {
          const val = gp[k] || 0;
          if (val === 0) return null;
          return (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: 'var(--c4)' }}>{label}</span>
              <strong style={{ color }}>{fmt(val)}</strong>
            </div>
          );
        })}
      </div>

      {/* Contas fixas do mês */}
      {contas.length > 0 && (
        <div className="c" style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Contas fixas</div>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', color: 'var(--c4)', marginBottom: 6 }}>
            <span>Previsto</span><strong>{fmt(dados?.totalPrevisto)}</strong>
          </div>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', color: 'var(--c4)', marginBottom: 8 }}>
            <span>Pago</span><strong style={{ color: (dados?.totalPago >= dados?.totalPrevisto * 0.9) ? 'var(--g)' : 'var(--r)' }}>{fmt(dados?.totalPago)}</strong>
          </div>
          {contas.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3, opacity: c.status === 'pago' ? 0.6 : 1 }}>
              <span>{STATUS_LABEL[c.status]} {c.nome}</span>
              <span style={{ color: STATUS_COLOR[c.status] }}>{fmt(c.valor_efetivo)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SUB_TABS = [
  { key: 'mes',        label: '📅 Mês' },
  { key: 'gastos',     label: '🧾 Gastos' },
  { key: 'contas',     label: '🏠 Contas fixas' },
  { key: 'precif',     label: '⚙️ Precificação' },
  { key: 'relatorio',  label: '📊 Relatórios' },
];

export default function FinanceiroPage({ onToast }) {
  const [sub, setSub] = useState('mes');

  return (
    <div>
      <div className="sub-tabs" style={{ marginBottom: 16 }}>
        {SUB_TABS.map(t => (
          <button key={t.key} className={sub === t.key ? 'on' : ''} onClick={() => setSub(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {sub === 'mes'       && <MesPage onToast={onToast} />}
      {sub === 'gastos'    && <GastosPage onToast={onToast} />}
      {sub === 'contas'    && <ContasFixasPage onToast={onToast} />}
      {sub === 'precif'    && <PrecificacaoForm onToast={onToast} />}
      {sub === 'relatorio' && <RelatorioFinanceiro />}
    </div>
  );
}
